import React, { useRef } from 'react';
import {
  Button,
  Popover,
  PopoverAnchor,
  PopoverContent,
} from '@billda/design-system';

interface RemoveSiteConfirmPopoverProps {
  anchorEl: HTMLElement | null;
  siteName: string;
  onClose: () => void;
  onConfirm: () => void;
  isRemoving?: boolean;
}

export const RemoveSiteConfirmPopover: React.FC<
  RemoveSiteConfirmPopoverProps
> = ({ anchorEl, siteName, onClose, onConfirm, isRemoving = false }) => {
  const open = Boolean(anchorEl);

  // Anchor the DS popover to the externally-owned anchor element.
  const anchorRef = useRef<HTMLElement | null>(null);
  anchorRef.current = anchorEl;

  const handleConfirm = () => {
    onConfirm();
    // Don't close here - parent will close after mutation completes
  };

  return (
    <Popover
      open={open}
      onOpenChange={nextOpen => {
        if (!nextOpen && !isRemoving) {
          onClose();
        }
      }}
    >
      <PopoverAnchor virtualRef={anchorRef} />
      <PopoverContent
        data-testid="remove-site-confirm-popover"
        className="mt-1 min-w-[280px] max-w-[320px]"
      >
        <div className="flex flex-col gap-4">
          <div>
            <p className="type-body-md mb-2 font-medium">Remove site access?</p>
            <p className="type-body-md text-content-secondary">
              Remove <strong>{siteName}</strong> from this user?
            </p>
          </div>

          <div className="flex justify-end gap-2">
            <Button
              size="sm"
              variant="ghost"
              onClick={onClose}
              disabled={isRemoving}
              data-testid="cancel-remove-button"
            >
              Cancel
            </Button>
            <Button
              size="sm"
              variant="danger"
              onClick={handleConfirm}
              disabled={isRemoving}
              data-testid="confirm-remove-button"
            >
              {isRemoving ? 'Removing...' : 'Remove'}
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};
